import { BWQuerySystem } from '@bayer-int/portletce-shared';
import * as t from 'io-ts';

import type { FilterType } from 'portlet/models';

import type { PortletQuery, PortletQueryParams } from '../types';
import { QueryType } from '../modules';

type CommonQueryFilterData = { key: string; value: string };

export type QueryFilterData = CommonQueryFilterData &
    (
        | { type: FilterType['HIERARCHY']; nodeName?: string; exclude: boolean; variant?: string }
        | { type: FilterType['DIMENSION']; exclude: boolean }
        | { type: FilterType['DATE'] }
        | { type: FilterType['DATE_RANGE'] }
        | { type: FilterType['ROLE'] }
        | { type: FilterType['NUMBER'] }
    );

export type QueryFilterDataItem = QueryFilterData & {
    varKey: string;
    compounded: boolean;
    object?: string;
};

export type QuerySettingDataItem = {
    key: string;
    type: SettingType;
};

export type ConditionQueryItems = {
    conditionQueryItem: QuerySettingDataItem | null;
    conditionQueryFilter: QueryFilterDataItem | null;
};

export enum SettingType {
    filter = 'filter',
    condition = 'condition',
    across = 'drill_x',
    down = 'drill_y',
}

export enum DrillDirection {
    across = 'x',
    down = 'y',
}

export enum DrillLogic {
    add = '+',
    only = '',
}

export const BWQueryC = t.intersection(
    [
        t.type({
            type: t.literal('BW'),
            id: t.string,
            querySettings: t.intersection([
                t.type({
                    allowMultipleDrills: t.boolean,
                    allowConditionChange: t.boolean,
                    drillDirection: t.keyof(DrillDirection),
                    conditionMaxValue: t.number,
                    conditionVariableName: t.string,
                }),
                t.partial({
                    defaultDecimals: t.union([t.null, t.number]),
                    system: t.keyof(BWQuerySystem),
                    drillLogic: t.keyof(DrillLogic),
                    jumpToAO: t.partial({
                        type: t.union([t.literal('QUERY'), t.literal('WORKBOOK')]),
                        target: t.string,
                    }),
                    enableDrillByNone: t.boolean,
                    autoDrillHierarchies: t.array(
                        t.type({
                            key: t.string,
                            label: t.string,
                            hierarchy: t.array(t.string),
                        }),
                    ),
                    keyfigureColumnName: t.union([
                        t.literal('technicalId'),
                        t.literal('name'),
                        t.literal('prefix'),
                    ]),
                    keyColumnPrefix: t.string,
                    textColumnPrefix: t.string,
                }),
            ]),
            drills: t.array(
                t.intersection([
                    t.type({
                        key: t.string,
                        label: t.string,
                        type: t.string,
                    }),
                    t.partial(
                        /** if not present, it's assumed to be true */
                        { drillable: t.boolean },
                    ),
                ]),
            ),
            filters: t.array(
                t.intersection([
                    t.type({
                        variable: t.string,
                        object: t.string,
                        to: t.string,
                    }),
                    t.partial({
                        // partial for backwards compatibility
                        varText: t.string,
                        visible: t.boolean,
                        filterable: t.boolean,
                        dateFormat: t.string,
                        compounded: t.boolean,
                        default: t.intersection([
                            t.type({
                                value: t.array(t.string),
                            }),
                            t.partial({
                                exclude: t.boolean,
                                nodeName: t.string,
                            }),
                        ]),
                    }),
                ]),
            ),
            conditions: t.array(
                t.intersection([
                    t.type({
                        key: t.string,
                        label: t.string,
                    }),
                    t.partial({
                        group: t.string,
                        defaultView: t.union([t.number, t.null]),
                    }),
                ]),
            ),
        }),
        //make `columns` and `rows` required after migrating portlets to new `QUERY` schema
        t.partial({
            columns: t.array(
                t.intersection([
                    t.type({
                        key: t.string,
                        text: t.string,
                    }),
                    //since we are not processing non keyfigures we should make these fields required after migrating portlets
                    t.partial({
                        applyScale: t.boolean,
                        decimals: t.union([t.number, t.null]),
                        datatype: t.keyof({ string: null, number: null }),
                    }),
                ]),
            ),
            rows: t.array(
                t.intersection([
                    t.type({
                        key: t.string,
                        text: t.string,
                    }),
                    //since we are not processing non keyfigures we should make these fields required after migrating portlets
                    t.partial({
                        applyScale: t.boolean,
                        decimals: t.union([t.number, t.null]),
                    }),
                ]),
            ),
        }),
    ],
    'BwQuery',
);

export type BWQuery = t.TypeOf<typeof BWQueryC>;

export type BWQueryParameters = { params: string; metadata: BWQuery };

export const isBWQuery = (query: PortletQuery): query is BWQuery => query.type === QueryType.BW;
export const areBWQueryParameters = (query: PortletQueryParams): query is BWQueryParameters =>
    query.metadata.type === 'BW';
